import { useLayoutEffect, useRef } from 'react';

import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { Typography } from '../Typography/Typography';

/**
 * localStorage key holding the "user dismissed the rebrand banner" flag.
 *
 * Deliberately a brand new, separately namespaced key. The existing
 * `PROVENANCE_*` keys (`PROVENANCE_TOKEN`, `PROVENANCE_USER`,
 * `PROVENANCE_LAST_HEARTBEAT`) are load-bearing auth/session state; a purely
 * cosmetic UI preference must not be stored under that namespace, and nothing
 * about dismissing a banner should ever be able to touch a session.
 */
export const REBRAND_BANNER_DISMISSED_KEY = 'LOVEBIRD_REBRAND_BANNER_DISMISSED';

/**
 * CSS custom property, set on `<html>`, holding the banner's rendered height
 * (`0px` while it is dismissed or unmounted).
 *
 * Layouts that pin chrome to the top of the window or size themselves to the
 * viewport subtract it, e.g. `top-[var(--rebrand-banner-height,0px)]` or
 * `h-[calc(100vh-var(--rebrand-banner-height,0px))]`. Tailwind only generates
 * classes it finds verbatim in the source, so those call sites spell the name
 * out instead of importing this constant — grep for it to find them all.
 */
export const REBRAND_BANNER_HEIGHT_VAR = '--rebrand-banner-height';

/**
 * Announcement bar for the Provenance → Lovebird rebrand.
 *
 * Mounted once, in `App`, as a `fixed` full-width bar pinned to the top of the
 * window above every layout shell. Dismissible, and the dismissal is persisted
 * in localStorage so it survives reloads and client-side navigation.
 *
 * Being `fixed`, it takes no space in the flow. Instead it publishes its
 * measured height as `REBRAND_BANNER_HEIGHT_VAR` (kept current by a
 * `ResizeObserver`, since the copy wraps on narrow phones); the app root pads
 * itself by that amount and each shell offsets its `fixed` / `top-0` /
 * viewport-height chrome by it. Once dismissed the variable is `0px`, so every
 * layout resolves to exactly its original values.
 *
 * `z-[1100]` is MUI's app-bar layer: above all page chrome (which tops out at
 * `z-[110]`), below MUI drawers (1200), modals (1300), snackbars and tooltips,
 * so overlays and their backdrops still cover it.
 */
export const RebrandBanner = () => {
  // `useLocalStorage` is an untyped legacy hook (it returns `any[]`); narrow it
  // at the call site so the rest of the component stays type-safe.
  const [isDismissed, setIsDismissed] = useLocalStorage(
    REBRAND_BANNER_DISMISSED_KEY,
    false
  ) as [boolean, (value: boolean) => void];

  const bannerRef = useRef<HTMLDivElement>(null);

  // A layout effect, so the offset is in place before the first paint and no
  // shell ever flashes underneath the bar.
  useLayoutEffect(() => {
    const rootStyle = document.documentElement.style;
    const banner = bannerRef.current;

    if (!banner) {
      rootStyle.setProperty(REBRAND_BANNER_HEIGHT_VAR, '0px');
      return;
    }

    const publishHeight = () =>
      rootStyle.setProperty(
        REBRAND_BANNER_HEIGHT_VAR,
        `${banner.getBoundingClientRect().height}px`
      );

    publishHeight();

    // Guarded because this renders at the app root: a browser without
    // ResizeObserver still gets the initial measurement instead of a crash.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(publishHeight);
    observer?.observe(banner);

    return () => {
      observer?.disconnect();
      rootStyle.setProperty(REBRAND_BANNER_HEIGHT_VAR, '0px');
    };
  }, [isDismissed]);

  if (isDismissed) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      role='region'
      aria-label='Site announcement'
      className='fixed inset-x-0 top-0 z-[1100] bg-forest-primary text-white'
    >
      <div className='flex w-full flex-row items-center justify-center px-10 py-2 sm:px-12'>
        <Typography
          type='body-200'
          className='text-center text-white'
        >
          {/*
            "Provenance" and "Lovebird" are hardcoded ON PURPOSE and must NOT be
            routed through `BRAND_NAME`. This sentence exists to announce the
            move from one specific name to another, so both names have to stay
            literal: if the brand constant changed later, interpolating it here
            would render nonsense such as "Lovebird is now Lovebird!".
          */}
          Provenance is now Lovebird! Same site, new name.
        </Typography>
      </div>

      <button
        type='button'
        aria-label='Dismiss announcement'
        onClick={() => setIsDismissed(true)}
        className='absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full p-2 text-white hover:bg-forest-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
      >
        <svg
          aria-hidden='true'
          focusable='false'
          width='12'
          height='12'
          viewBox='0 0 12 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M1 1L11 11M11 1L1 11'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
          />
        </svg>
      </button>
    </div>
  );
};
